import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { staffService } from '@/services/staff.service';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Skeleton,
  Select,
  DatePicker,
  EmptyState,
  ErrorBoundary,
  BarChart,
  PieChart,
  Button,
  Alert,
} from '@bbpos/ui';
import type { DateRange } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import {
  useConversionRate,
  useQuotations,
  QuotationStatusBadge,
  QuotationHero,
  QuotationKpi,
} from '@/features/quotations';
import type { KpiDelta, Quotation, QuotationStatus } from '@/features/quotations';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import {
  FileTextIcon,
  CheckCircleIcon,
  TrendingUpIcon,
  ClockIcon,
  CurrencyIcon,
  ChartIcon,
  PulseIcon,
  CalendarIcon,
  MapPinIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  FunnelIcon,
  DownloadIcon,
} from './icons';

// ─── Types & helpers ────────────────────────────────────────────────────────

type RangePreset = 'today' | '7d' | '30d' | 'mtd' | 'custom';

const PRESET_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: 'mtd', label: 'MTD' },
  { value: 'custom', label: 'Custom' },
];

// Formats a Date as a calendar `YYYY-MM-DD` string using its LOCAL components.
// Using `d.toISOString().split('T')[0]` would convert to UTC first, so in any
// timezone east of UTC (e.g. Asia/Kolkata, UTC+5:30) local midnight maps to the
// previous calendar day, shifting every preset/custom range back one day
// (issue #206). Reading local Y/M/D keeps the date the user actually sees.
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// A `YYYY-MM-DD` store day as "21 Oct", from its own digits — `new Date('2026-10-21')`
// is UTC midnight and shows the day before west of UTC (SS2-007).
export function formatDayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function resolveRange(
  preset: RangePreset,
  custom: { from?: string; to?: string },
): { start_date: string | undefined; end_date: string | undefined; label: string } {
  if (preset === 'custom') {
    if (custom.from && custom.to) {
      return {
        start_date: custom.from,
        end_date: custom.to,
        label: `${formatShortDate(custom.from)} – ${formatShortDate(custom.to)}`,
      };
    }
    return { start_date: undefined, end_date: undefined, label: 'All time' };
  }

  const now = new Date();
  const end = toIsoDate(now);
  const start = new Date(now);

  switch (preset) {
    case 'today':
      return { start_date: end, end_date: end, label: 'Today' };
    case '7d':
      start.setDate(start.getDate() - 6);
      return { start_date: toIsoDate(start), end_date: end, label: 'Last 7 days' };
    case '30d':
      start.setDate(start.getDate() - 29);
      return { start_date: toIsoDate(start), end_date: end, label: 'Last 30 days' };
    case 'mtd':
      start.setDate(1);
      return { start_date: toIsoDate(start), end_date: end, label: 'Month to date' };
  }
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function formatHoursAuto(hours: number): string {
  if (!hours || hours <= 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} hrs`;
  return `${(hours / 24).toFixed(1)} days`;
}

function formatRelativeFuture(target: string): {
  label: string;
  severity: 'urgent' | 'soon' | 'ok';
} {
  const now = Date.now();
  const t = new Date(target).getTime();
  const diffMs = t - now;
  if (diffMs <= 0) return { label: 'Expired', severity: 'urgent' };
  const hrs = diffMs / (1000 * 60 * 60);
  if (hrs < 24) {
    const h = Math.floor(hrs);
    const m = Math.floor((hrs - h) * 60);
    return { label: `in ${h}h ${m}m`, severity: 'urgent' };
  }
  const days = Math.floor(hrs / 24);
  const remHrs = Math.floor(hrs - days * 24);
  if (days < 3) return { label: `in ${days}d ${remHrs}h`, severity: 'urgent' };
  if (days <= 7) return { label: `in ${days}d`, severity: 'soon' };
  return { label: `in ${days}d`, severity: 'ok' };
}

function formatRelativePast(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 60_000) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatShortDate(iso.split('T')[0]);
}

/**
 * Freshness badge state for the hero. The badge must reflect fetch *success*,
 * not merely that a query resolved: when the latest refetch failed (`isError`)
 * we may still be showing stale data from a prior success, so the badge must not
 * claim the data is "live/just updated" (#210). `dataUpdatedAt` is React Query's
 * last-*successful*-load timestamp, so it stays put on a failed refetch.
 */
export function freshnessBadge(
  dataUpdatedAt: number | undefined,
  isError: boolean,
): { live: boolean; label: string } {
  const ago = dataUpdatedAt
    ? formatRelativePast(new Date(dataUpdatedAt).toISOString())
    : '—';
  if (isError) {
    return { live: false, label: `Stale — last updated ${ago}` };
  }
  return { live: true, label: `Updated ${ago}` };
}

/**
 * The moment a quote actually became a sale. `converted_at` is stamped at
 * conversion time; fall back to `updated_at`/`created_at` only for legacy rows
 * that predate the column so ranking never crashes on a null.
 */
export function conversionTime(q: Quotation): string {
  return q.converted_at ?? q.updated_at ?? q.created_at;
}

/**
 * Order CONVERTED quotes for the "Recent Conversions" feed by when each was
 * actually converted (newest first) — NOT by `updated_at`, which any later
 * edit/audit write bumps and would falsely float an old conversion to the top.
 */
export function sortRecentConversions(quotes: Quotation[]): Quotation[] {
  return [...quotes]
    .sort((a, b) => new Date(conversionTime(b)).getTime() - new Date(conversionTime(a)).getTime())
    .slice(0, 8);
}

// ─── CSV export ─────────────────────────────────────────────────────────────

interface CsvRow {
  [key: string]: string | number;
}

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(rows: CsvRow[], filename: string): void {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => csvEscape(row[h])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Pipeline Trend (unified line chart, Created vs Converted) ──────────────

interface TrendPoint {
  label: string;
  created: number;
  converted: number;
}

function PipelineTrendChart({ data }: { data: TrendPoint[] }) {
  const W = 560;
  const H = 200;
  const PAD = { top: 16, right: 16, bottom: 28, left: 40 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(...data.map((d) => Math.max(d.created, d.converted)), 1);
  const yTicks = [0, 0.5, 1].map((pct) => ({
    y: PAD.top + innerH - pct * innerH,
    label: Math.round(maxVal * pct).toLocaleString('en-IN'),
  }));

  function toPoints(key: 'created' | 'converted'): string {
    if (!data.length) return '';
    return data
      .map((d, i) => {
        const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
        const y = PAD.top + innerH - (d[key] / maxVal) * innerH;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  // Build area path under the Created line to emphasise the pipeline volume
  const areaPath = (() => {
    if (!data.length) return '';
    const pts = data.map((d, i) => {
      const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
      const y = PAD.top + innerH - (d.created / maxVal) * innerH;
      return { x, y };
    });
    const start = `M ${pts[0].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)}`;
    const line = pts.map((p) => `L ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const end = `L ${pts[pts.length - 1].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;
    return `${start} ${line} ${end}`;
  })();

  const createdPoints = toPoints('created');
  const convertedPoints = toPoints('converted');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      role="img"
      aria-label="Pipeline vs Conversions trend"
    >
      <defs>
        <linearGradient id="pipelineFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Grid */}
      {yTicks.map((t) => (
        <line
          key={`grid-${t.y}`}
          x1={PAD.left}
          y1={t.y}
          x2={W - PAD.right}
          y2={t.y}
          stroke="currentColor"
          strokeWidth="1"
          className="text-neutral-200 dark:text-neutral-700/60"
        />
      ))}
      {yTicks.map((t) => (
        <text
          key={`yl-${t.y}`}
          x={PAD.left - 6}
          y={t.y + 3}
          textAnchor="end"
          fontSize="10"
          className="fill-neutral-400 dark:fill-neutral-500 font-mono tabular-nums"
        >
          {t.label}
        </text>
      ))}

      {/* X-axis labels */}
      {data.map((d, i) => {
        const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
        const show = data.length <= 8 || i % Math.ceil(data.length / 8) === 0;
        return show ? (
          <text
            key={`xl-${i}`}
            x={x}
            y={H - 6}
            textAnchor="middle"
            fontSize="10"
            className="fill-neutral-400 dark:fill-neutral-500 font-mono"
          >
            {d.label}
          </text>
        ) : null;
      })}

      {/* Created area */}
      {areaPath && <path d={areaPath} fill="url(#pipelineFill)" />}

      {/* Created line (indigo) */}
      {createdPoints && (
        <polyline points={createdPoints} fill="none" stroke="#6366f1" strokeWidth="2" />
      )}

      {/* Converted line (success green, dashed) */}
      {convertedPoints && (
        <polyline
          points={convertedPoints}
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeDasharray="5 3"
        />
      )}

      {/* Data points with tooltips */}
      {data.map((d, i) => {
        const x = PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
        const yC = PAD.top + innerH - (d.created / maxVal) * innerH;
        const yV = PAD.top + innerH - (d.converted / maxVal) * innerH;
        return (
          <g key={`pt-${i}`}>
            <circle cx={x} cy={yC} r="3" fill="#6366f1">
              <title>{`${d.label} — Created: ${d.created}`}</title>
            </circle>
            <circle cx={x} cy={yV} r="3" fill="#10b981">
              <title>{`${d.label} — Converted: ${d.converted}`}</title>
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Funnel ─────────────────────────────────────────────────────────────────

interface FunnelStep {
  label: string;
  value: number;
  rupeeValue: number;
  color: string;
  textColor: string;
}

// Every stage is a share of the quotes created in the period (SS2-007): the
// stages are not disjoint steps, so a stage-to-stage ratio read "Converted 275%".
export function ConversionFunnel({
  total,
  sent,
  accepted,
  converted,
  dropped,
  quotedValue,
  sentValue,
  acceptedValue,
  convertedValue,
  droppedValue,
}: {
  total: number;
  sent: number;
  accepted: number;
  converted: number;
  dropped: number;
  quotedValue: number;
  sentValue: number;
  acceptedValue: number;
  convertedValue: number;
  droppedValue: number;
}) {
  const currency = useTenantCurrency();
  const max = Math.max(total, 1);
  const steps: FunnelStep[] = [
    {
      label: 'Created',
      value: total,
      rupeeValue: quotedValue,
      color: 'bg-gradient-to-r from-brand-500 to-brand-600',
      textColor: 'text-brand-700 dark:text-brand-200',
    },
    {
      label: 'Sent',
      value: sent,
      rupeeValue: sentValue,
      color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      textColor: 'text-indigo-700 dark:text-indigo-200',
    },
    {
      label: 'Accepted',
      value: accepted,
      rupeeValue: acceptedValue,
      color: 'bg-gradient-to-r from-teal-500 to-teal-600',
      textColor: 'text-teal-700 dark:text-teal-200',
    },
    {
      label: 'Converted',
      value: converted,
      rupeeValue: convertedValue,
      color: 'bg-gradient-to-r from-success-500 to-success-600',
      textColor: 'text-success-700 dark:text-success-200',
    },
  ];
  const shareOfTotal = (n: number) => (total > 0 ? ((n / total) * 100).toFixed(0) : '0');

  return (
    <div className={cn(GLASS, 'p-6 animate-slide-up')}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center shadow-[0_6px_16px_-8px_rgba(99,102,241,0.6)]">
            <FunnelIcon className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
              Pipeline Funnel
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Each stage as a share of the quotes created
            </p>
          </div>
        </div>
        {dropped > 0 && (
          <div className="hidden sm:flex flex-col items-end text-right">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-danger-600 dark:text-danger-400">
              Value lost (dropped)
            </span>
            <span className="font-mono text-base font-bold text-danger-700 dark:text-danger-300 tabular-nums">
              {formatCurrency(droppedValue, currency)}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {steps.map((step) => {
          const widthPct = Math.max(8, (step.value / max) * 100);

          return (
            <div key={step.label} className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between mb-1.5 gap-3">
                  <span className="text-[11px] font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wide truncate">
                    {step.label}
                  </span>
                  <div className="flex items-baseline gap-3 shrink-0">
                    <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      {formatCurrency(step.rupeeValue, currency)}
                    </span>
                    <span
                      className={`font-mono text-base font-semibold tabular-nums ${step.textColor}`}
                    >
                      {step.value.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
                <div className="relative h-9 rounded-lg bg-neutral-100/60 dark:bg-neutral-800/40 overflow-hidden">
                  <div
                    className={`h-full ${step.color} rounded-lg transition-[width] duration-500 ease-out shadow-[inset_0_-4px_8px_rgba(0,0,0,0.08)]`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
              <div className="w-16 text-right shrink-0">
                <span className="font-mono text-xs font-semibold text-neutral-600 dark:text-neutral-300 tabular-nums">
                  {shareOfTotal(step.value)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dropped terminal — declined + expired + cancelled, never converted */}
      {dropped > 0 && (
        <div className="mt-4 pl-4 border-l-2 border-dashed border-danger-300/70 dark:border-danger-800/80">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between mb-1.5 gap-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-danger-700 dark:text-danger-300">
                  <AlertTriangleIcon className="h-3 w-3" />
                  Dropped (declined, expired, cancelled)
                </span>
                <div className="flex items-baseline gap-3 shrink-0">
                  <span className="font-mono text-xs text-danger-600 dark:text-danger-400 tabular-nums">
                    {formatCurrency(droppedValue, currency)}
                  </span>
                  <span className="font-mono text-base font-semibold text-danger-700 dark:text-danger-300 tabular-nums">
                    {dropped.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
              <div className="relative h-6 rounded-lg bg-danger-100/50 dark:bg-danger-900/20 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-danger-500 to-danger-600 rounded-lg transition-[width] duration-500 ease-out shadow-[inset_0_-4px_8px_rgba(0,0,0,0.08)]"
                  style={{ width: `${Math.max(8, (dropped / max) * 100)}%` }}
                />
              </div>
            </div>
            <div className="w-16 text-right shrink-0">
              <span className="font-mono text-xs font-semibold text-danger-600 dark:text-danger-400 tabular-nums">
                {shareOfTotal(dropped)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Status Breakdown Table ─────────────────────────────────────────────────

interface StatusRowConfig {
  status: QuotationStatus;
  count: number;
  estValue: number;
}

function StatusBreakdownTable({
  rows,
  totalCount,
}: {
  rows: StatusRowConfig[];
  totalCount: number;
}) {
  const currency = useTenantCurrency();
  return (
    <div className="overflow-hidden rounded-xl bg-neutral-50/50 dark:bg-neutral-800/30">
      <table className="w-full text-sm">
        <thead className="bg-neutral-100/40 dark:bg-neutral-800/40">
          <tr>
            <th className="text-left text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 py-2">
              Status
            </th>
            <th className="text-right text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 py-2">
              Count
            </th>
            <th className="text-right text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 py-2">
              Share
            </th>
            <th className="text-right text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 px-3 py-2">
              Est. Value
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const share = totalCount > 0 ? (r.count / totalCount) * 100 : 0;
            const rowTint = idx % 2 === 0 ? '' : 'bg-neutral-100/30 dark:bg-neutral-800/30';
            return (
              <tr key={r.status} className={rowTint}>
                <td className="px-3 py-2.5">
                  <QuotationStatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-medium text-neutral-800 dark:text-neutral-100">
                  {r.count.toLocaleString('en-IN')}
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                  {share.toFixed(1)}%
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-medium text-neutral-700 dark:text-neutral-200">
                  {r.estValue > 0 ? formatCurrency(r.estValue, currency) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Watchlist tables (Expiring + Recent) ───────────────────────────────────

function ExpiringSoonTable({ data, loading }: { data: Quotation[]; loading: boolean }) {
  const currency = useTenantCurrency();
  if (loading && data.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={48} className="rounded-lg" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <div className="w-10 h-10 rounded-full bg-success-50 dark:bg-success-900/20 flex items-center justify-center text-success-500">
          <CheckCircleIcon className="h-5 w-5" />
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          No quotes expiring in the next 7 days.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            <th className="text-left px-2 py-2">Quote #</th>
            <th className="text-left px-2 py-2">Customer</th>
            <th className="text-right px-2 py-2">Value</th>
            <th className="text-right px-2 py-2">Valid Until</th>
            <th className="text-right px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((q, idx) => {
            const rel = formatRelativeFuture(q.valid_until);
            const sevTone =
              rel.severity === 'urgent'
                ? 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300'
                : rel.severity === 'soon'
                  ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                  : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
            const rowTint = idx % 2 === 0 ? '' : 'bg-neutral-50/40 dark:bg-neutral-800/30';
            return (
              <tr key={q.id} className={`${rowTint} hover:bg-brand-50/40 dark:hover:bg-brand-900/10 transition-colors`}>
                <td className="px-2 py-2.5">
                  <Link
                    to={`/quotations/${q.id}`}
                    className="font-mono font-medium text-brand-600 dark:text-brand-400 hover:underline tabular-nums"
                  >
                    {q.quote_number}
                  </Link>
                </td>
                <td className="px-2 py-2.5 text-neutral-700 dark:text-neutral-200 truncate max-w-[180px]">
                  {q.customer_name ?? <span className="italic text-neutral-400">Walk-in</span>}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums font-medium text-neutral-800 dark:text-neutral-100">
                  {formatCurrency(q.grand_total, currency)}
                </td>
                <td className="px-2 py-2.5 text-right">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold font-mono tabular-nums ${sevTone}`}
                  >
                    {rel.severity !== 'ok' && (
                      <span
                        className={`h-1.5 w-1.5 rounded-full bg-current ${rel.severity === 'urgent' ? 'animate-pulse' : ''}`}
                      />
                    )}
                    {rel.label}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-right">
                  <Link
                    to={`/quotations/${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
                    aria-label={`Open quotation ${q.quote_number}`}
                  >
                    Open <ArrowRightIcon className="h-3 w-3" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RecentConversionsTable({ data, loading }: { data: Quotation[]; loading: boolean }) {
  const currency = useTenantCurrency();
  if (loading && data.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={48} className="rounded-lg" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-400">
          <CheckCircleIcon className="h-5 w-5" />
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          No conversions yet in the selected scope.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            <th className="text-left px-2 py-2">Quote #</th>
            <th className="text-left px-2 py-2">Customer</th>
            <th className="text-right px-2 py-2">Converted Value</th>
            <th className="text-right px-2 py-2">When</th>
            <th className="text-right px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((q, idx) => {
            const rowTint = idx % 2 === 0 ? '' : 'bg-neutral-50/40 dark:bg-neutral-800/30';
            return (
              <tr
                key={q.id}
                className={`${rowTint} hover:bg-success-50/40 dark:hover:bg-success-900/10 transition-colors`}
              >
                <td className="px-2 py-2.5">
                  <Link
                    to={`/quotations/${q.id}`}
                    className="font-mono font-medium text-brand-600 dark:text-brand-400 hover:underline tabular-nums"
                  >
                    {q.quote_number}
                  </Link>
                </td>
                <td className="px-2 py-2.5 text-neutral-700 dark:text-neutral-200 truncate max-w-[180px]">
                  {q.customer_name ?? <span className="italic text-neutral-400">Walk-in</span>}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums font-medium text-success-700 dark:text-success-300">
                  {formatCurrency(q.grand_total, currency)}
                </td>
                <td className="px-2 py-2.5 text-right text-xs text-neutral-500 dark:text-neutral-400">
                  {formatRelativePast(conversionTime(q))}
                </td>
                <td className="px-2 py-2.5 text-right">
                  <Link
                    to={`/quotations/${q.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
                    aria-label={`View converted quotation ${q.quote_number}`}
                  >
                    View <ArrowRightIcon className="h-3 w-3" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function QuotationDashboardContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const profile = useAuthStore((s) => s.profile);
  const fallbackLocationId = useDashboardStore((s) => s.selectedLocationId);

  // ── Filter state from URL ──────────────────────────────────────────────────

  const rawPreset = (searchParams.get('range') as RangePreset | null) ?? null;
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const locationParam = searchParams.get('location') ?? '';
  const cashierParam = searchParams.get('cashier') ?? '';

  const preset: RangePreset = rawPreset ?? (from && to ? 'custom' : '7d');
  const range = resolveRange(preset, { from: from || undefined, to: to || undefined });
  const effectiveLocationId = locationParam || fallbackLocationId || undefined;

  function setPreset(next: RangePreset) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('range', next);
      if (next !== 'custom') {
        sp.delete('from');
        sp.delete('to');
      }
      return sp;
    });
  }

  function setCustomRange(rangeVal: DateRange | undefined) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('range', 'custom');
      if (rangeVal?.from && rangeVal?.to) {
        sp.set('from', toIsoDate(rangeVal.from));
        sp.set('to', toIsoDate(rangeVal.to));
      } else {
        sp.delete('from');
        sp.delete('to');
      }
      return sp;
    });
  }

  function setLocation(value: string) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (value) sp.set('location', value);
      else sp.delete('location');
      return sp;
    });
  }

  function setCashier(value: string) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (value) sp.set('cashier', value);
      else sp.delete('cashier');
      return sp;
    });
  }

  function resetFilters() {
    setSearchParams(new URLSearchParams());
  }

  // ── Data hooks ─────────────────────────────────────────────────────────────

  const conversionParams = {
    location_id: effectiveLocationId,
    created_by: cashierParam || undefined,
    start_date: range.start_date,
    end_date: range.end_date,
  };

  const { data, isLoading, isError, dataUpdatedAt, refetch } =
    useConversionRate(conversionParams);

  // Staff who can raise quotes, for the "by cashier" filter.
  const staffQuery = useQuery({
    queryKey: ['staff', 'quote-filter'],
    queryFn: () => staffService.listPerformance({ limit: 100 }),
    staleTime: 5 * 60_000,
  });

  // Watchlist queries — independent of date range (operator needs them anytime).
  // Order by valid_until ascending so the soonest-to-lapse active quotes are
  // fetched first — otherwise the 50-row cap silently drops older-but-expiring
  // quotes ordered behind newer ones (issue #207).
  const expiringQuery = useQuotations({
    status: 'ACTIVE',
    location_id: effectiveLocationId,
    limit: 50,
    page: 1,
    sort: 'valid_until_asc',
  });

  const recentConvertedQuery = useQuotations({
    status: 'CONVERTED',
    location_id: effectiveLocationId,
    limit: 10,
    page: 1,
  });

  // ── Derived: filter expiring within 7 days, sort by valid_until asc ────────
  const expiringSoon = useMemo(() => {
    const all = expiringQuery.data?.data ?? [];
    const cutoff = Date.now() + 7 * 24 * 60 * 60 * 1000;
    return all
      .filter((q) => {
        const t = new Date(q.valid_until).getTime();
        return t > Date.now() && t <= cutoff;
      })
      .sort((a, b) => new Date(a.valid_until).getTime() - new Date(b.valid_until).getTime())
      .slice(0, 8);
  }, [expiringQuery.data]);

  const recentConverted = useMemo(
    () => sortRecentConversions(recentConvertedQuery.data?.data ?? []),
    [recentConvertedQuery.data],
  );

  // ── Loading skeleton (initial) ─────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={112} className="rounded-2xl mb-6" />
        <Skeleton variant="rect" height={64} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-2xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={220} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Skeleton variant="rect" height={280} className="rounded-2xl" />
          <Skeleton variant="rect" height={280} className="rounded-2xl" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton variant="rect" height={300} className="rounded-2xl" />
          <Skeleton variant="rect" height={300} className="rounded-2xl" />
        </div>
      </>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  // The dashboard is built entirely on the conversion-rate response. When that
  // fetch fails (network drop, 5xx, or an environment where the endpoint isn't
  // deployed → 404/501), surface a clear, retryable error rather than falling
  // through to the "No quotes in this period" empty state, which would falsely
  // imply an empty pipeline (issue #209).
  if (isError && !data) {
    return (
      <div className="animate-fade-in">
        <Alert
          variant="danger"
          title="Couldn't load the quote dashboard"
          description="We couldn't fetch quotation analytics. Check your connection and try again."
          action={{ label: 'Retry', onClick: () => void refetch() }}
        />
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  const totalQuotes = data?.total_quotes ?? 0;
  const converted = data?.converted ?? 0;
  const expired = data?.expired ?? 0;
  const active = data?.active ?? 0;
  const approved = data?.approved ?? 0;
  const cancelled = data?.cancelled ?? 0;
  const conversionRate = data?.conversion_rate_pct ?? 0;
  const avgTime = data?.avg_conversion_time_hours ?? 0;
  const quotedValue = data?.total_quoted_value ?? 0;
  const convertedValue = data?.total_converted_value ?? 0;
  const activeValue = data?.total_active_value ?? 0;
  const expiredValue = data?.total_expired_value ?? 0;
  const byPeriod = data?.by_period ?? [];
  const prev = data?.prev_period ?? null;

  const hasData = totalQuotes > 0;
  const valueShare = quotedValue > 0 ? (convertedValue / quotedValue) * 100 : 0;
  const avgQuotedValue = totalQuotes > 0 ? Math.round(quotedValue / totalQuotes) : 0;

  // Remaining value stored against APPROVED/CANCELLED is derived from the residual.
  // Currently the API returns only per-status values for ACTIVE/CONVERTED/EXPIRED —
  // any leftover sits with APPROVED+CANCELLED and we estimate by avg × count.
  const approvedEstValue = avgQuotedValue * approved;
  const cancelledEstValue = avgQuotedValue * cancelled;

  const allStatusRows: StatusRowConfig[] = [
    { status: 'ACTIVE', count: active, estValue: activeValue },
    { status: 'APPROVED', count: approved, estValue: approvedEstValue },
    { status: 'CONVERTED', count: converted, estValue: convertedValue },
    { status: 'EXPIRED', count: expired, estValue: expiredValue },
    { status: 'CANCELLED', count: cancelled, estValue: cancelledEstValue },
  ];
  const statusRows = allStatusRows.filter((r) => r.count > 0);

  // Delta pill helper — comparing current period to previous period.
  // Returns null when previous is absent or zero (avoid fake "+100%" noise).
  function buildDelta(
    current: number,
    previous: number | undefined | null,
    formatter: (n: number) => string,
    opts?: { higherIsBetter?: boolean },
  ): KpiDelta | undefined {
    if (previous == null || previous === 0) return undefined;
    const diff = current - previous;
    if (diff === 0) return { direction: 'flat', text: 'no change' };
    const pct = (diff / previous) * 100;
    const direction: KpiDelta['direction'] = diff > 0 ? 'up' : 'down';
    const higherIsBetter = opts?.higherIsBetter ?? true;
    const positive = higherIsBetter ? diff > 0 : diff < 0;
    return {
      direction,
      positive,
      text: `${formatter(Math.abs(diff))} (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%)`,
    };
  }

  const fmtCount = (n: number) => n.toLocaleString('en-IN');
  const fmtPct = (n: number) => `${n.toFixed(1)}pp`;

  const deltaTotal = buildDelta(totalQuotes, prev?.total_quotes, fmtCount);
  const deltaConverted = buildDelta(converted, prev?.converted, fmtCount);
  const deltaRate = buildDelta(conversionRate, prev?.conversion_rate_pct, fmtPct);
  // Not a bare `formatCurrency` reference: buildDelta calls it with one
  // argument, which would silently fall back to the INR default.
  const deltaQuotedValue = buildDelta(quotedValue, prev?.total_quoted_value, (n) =>
    formatCurrency(n, currency),
  );

  // Chart data
  const trendData: TrendPoint[] = byPeriod.map((p) => ({
    label: formatDayLabel(p.date),
    created: p.quotes,
    converted: p.converted,
  }));

  const rateBarData = byPeriod.map((p) => ({
    label: formatDayLabel(p.date),
    value: Math.round(p.rate_pct),
    color: '#8b5cf6',
  }));

  const pieData = [
    { label: 'Active', value: active, color: '#14b8a6' },
    { label: 'Approved', value: approved, color: '#6366f1' },
    { label: 'Converted', value: converted, color: '#10b981' },
    { label: 'Expired', value: expired, color: '#ef4444' },
    { label: 'Cancelled', value: cancelled, color: '#9ca3af' },
  ].filter((s) => s.value > 0);

  // Location options (tenant locations only — platform users see "All locations" only)
  const locationOptions = [
    { value: '', label: 'All locations' },
    ...((profile?.locations ?? [])
      .filter((l) => l.is_active)
      .map((l) => ({ value: l.id, label: l.name }))),
  ];

  const cashierOptions = [
    { value: '', label: 'All cashiers' },
    ...(staffQuery.data?.data.rows ?? []).map((s) => ({ value: s.user_id, label: s.user_name })),
  ];

  const locationLabel =
    locationOptions.find((o) => o.value === (locationParam ?? ''))?.label ?? 'All locations';

  const freshness = freshnessBadge(dataUpdatedAt, isError);
  const customRange: DateRange | undefined =
    from && to ? { from: new Date(from), to: new Date(to) } : undefined;

  function exportSummaryCsv() {
    const rows: CsvRow[] = [
      { metric: 'Total Quotes', value: totalQuotes },
      { metric: 'Converted', value: converted },
      { metric: 'Active', value: active },
      { metric: 'Approved', value: approved },
      { metric: 'Expired', value: expired },
      { metric: 'Cancelled', value: cancelled },
      { metric: 'Conversion Rate (%)', value: conversionRate.toFixed(2) },
      { metric: 'Avg Time to Convert (hrs)', value: avgTime.toFixed(2) },
      { metric: `Total Quoted Value (${symbol})`, value: (quotedValue / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
      { metric: `Converted Value (${symbol})`, value: (convertedValue / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
      { metric: `Active Value (${symbol})`, value: (activeValue / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
      { metric: `Expired Value at Risk (${symbol})`, value: (expiredValue / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
    ];
    if (prev) {
      rows.push({ metric: '—', value: '' });
      rows.push({ metric: 'Prev Period Total Quotes', value: prev.total_quotes });
      rows.push({ metric: 'Prev Period Converted', value: prev.converted });
      rows.push({ metric: 'Prev Period Conversion Rate (%)', value: prev.conversion_rate_pct.toFixed(2) });
      rows.push({ metric: `Prev Period Quoted Value (${symbol})`, value: (prev.total_quoted_value / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) });
    }
    if (byPeriod.length > 0) {
      rows.push({ metric: '—', value: '' });
      for (const p of byPeriod) {
        rows.push({
          metric: `Day ${p.date}`,
          value: `quotes=${p.quotes} converted=${p.converted} rate=${p.rate_pct.toFixed(1)}%`,
        });
      }
    }
    const today = new Date().toISOString().split('T')[0];
    downloadCsv(rows, `quotation-analytics-${today}.csv`);
  }

  return (
    <>
      {/* Standardized hero — same gradient as List/Detail */}
      <QuotationHero
        title="Quote Dashboard"
        subtitle="Pipeline, conversion velocity, and value at risk."
        right={
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/90">
            <span className="inline-flex items-center gap-1.5">
              <CalendarIcon className="opacity-80" />
              <span className="font-mono tabular-nums">{range.label}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPinIcon className="opacity-80" />
              <span className="truncate max-w-[180px]">{locationLabel}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  freshness.live ? 'bg-emerald-300 animate-pulse' : 'bg-amber-300',
                )}
              />
              <span className="font-mono tabular-nums">{freshness.label}</span>
            </span>
            <Button
              variant="secondary"
              onClick={exportSummaryCsv}
              disabled={!hasData}
              className="bg-white/15 hover:bg-white/25 text-white border-white/20"
            >
              <DownloadIcon className="h-3.5 w-3.5 mr-1.5" />
              Export CSV
            </Button>
          </div>
        }
      />

      {/* Filter bar — sticky, glass surface, no border */}
      <div className={cn(GLASS_FILTER, 'sticky top-0 z-10 mb-6 p-3')}>
        <div className="flex flex-wrap items-center gap-3">
          {/* Preset segmented control */}
          <div className="inline-flex p-1 rounded-lg bg-neutral-100/60 dark:bg-neutral-800/50">
            {PRESET_OPTIONS.map((opt) => {
              const active = preset === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPreset(opt.value)}
                  aria-pressed={active}
                  className={[
                    'px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
                    active
                      ? 'bg-white dark:bg-neutral-900 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white',
                  ].join(' ')}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Custom date range (only when preset=custom) */}
          {preset === 'custom' && (
            <div className="w-64">
              <DatePicker
                variant="range"
                value={customRange}
                onChange={(r) => setCustomRange(r as DateRange | undefined)}
                placeholder="Pick a date range"
              />
            </div>
          )}

          {/* Location filter */}
          <div className="w-56">
            <Select
              options={locationOptions}
              value={locationParam}
              onChange={(v) => setLocation(typeof v === 'string' ? v : '')}
              placeholder="All locations"
              clearable
            />
          </div>

          {/* Cashier filter — who raised the quotes */}
          <div className="w-56">
            <Select
              options={cashierOptions}
              value={cashierParam}
              onChange={(v) => setCashier(typeof v === 'string' ? v : '')}
              placeholder="All cashiers"
              clearable
            />
          </div>

          {/* Reset */}
          <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto">
            Reset
          </Button>
        </div>
      </div>

      {!hasData ? (
        <EmptyState
          variant="page"
          icon={<ChartIcon className="h-full w-full" />}
          title="No quotes in this period"
          description="Adjust the filters above or head to the quote list to see all activity."
          action={{ label: 'View all quotes', onClick: () => navigate('/quotations') }}
        />
      ) : (
        <>
          {/* KPI Grid (8 cards) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
            <QuotationKpi
              tone="brand"
              title="Total Quotes"
              value={totalQuotes.toLocaleString('en-IN')}
              icon={<FileTextIcon />}
              delta={deltaTotal}
              delay={0}
            />
            <QuotationKpi
              tone="violet"
              title="Conversion Rate"
              value={`${conversionRate.toFixed(1)}%`}
              subtext={converted > 0 ? `${converted} of ${totalQuotes} converted` : undefined}
              icon={<TrendingUpIcon />}
              delta={deltaRate}
              delay={50}
            />
            <QuotationKpi
              tone="success"
              title="Converted"
              value={converted.toLocaleString('en-IN')}
              icon={<CheckCircleIcon />}
              delta={deltaConverted}
              delay={100}
            />
            <QuotationKpi
              tone="teal"
              title="Active"
              value={active.toLocaleString('en-IN')}
              subtext="in pipeline"
              icon={<PulseIcon />}
              delay={150}
            />
            <QuotationKpi
              tone="danger"
              title="Expired"
              value={expired.toLocaleString('en-IN')}
              subtext={expired > 0 ? 'value at risk lapsed' : undefined}
              subtextTone={expired > 0 ? 'warning' : 'neutral'}
              icon={<AlertTriangleIcon />}
              delay={200}
            />
            <QuotationKpi
              tone="indigo"
              title="Avg Time to Convert"
              value={formatHoursAuto(avgTime)}
              icon={<ClockIcon />}
              delay={250}
            />
            <QuotationKpi
              tone="teal"
              title="Total Quoted Value"
              value={formatCurrency(quotedValue, currency)}
              icon={<CurrencyIcon />}
              delta={deltaQuotedValue}
              delay={300}
            />
            <QuotationKpi
              tone="success"
              title="Converted Value"
              value={formatCurrency(convertedValue, currency)}
              subtext={quotedValue > 0 ? `${valueShare.toFixed(1)}% of quoted` : undefined}
              subtextTone="success"
              icon={<CurrencyIcon />}
              delay={350}
            />
          </div>

          {/* Conversion Funnel */}
          <div className="mb-6">
            <ConversionFunnel
              total={totalQuotes}
              sent={data?.sent ?? 0}
              accepted={data?.accepted ?? 0}
              converted={converted}
              dropped={data?.dropped ?? 0}
              quotedValue={quotedValue}
              sentValue={data?.total_sent_value ?? 0}
              acceptedValue={data?.total_accepted_value ?? 0}
              convertedValue={convertedValue}
              droppedValue={data?.total_dropped_value ?? 0}
            />
          </div>

          {/* Trend charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className={cn(GLASS, 'p-6 animate-slide-up')}>
              <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                    Pipeline vs Conversions
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Quotes created vs. converted by day
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-neutral-600 dark:text-neutral-300 shrink-0">
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="18" height="4" aria-hidden="true">
                      <line x1="0" y1="2" x2="18" y2="2" stroke="#6366f1" strokeWidth="2" />
                    </svg>
                    Created
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="18" height="4" aria-hidden="true">
                      <line
                        x1="0"
                        y1="2"
                        x2="18"
                        y2="2"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeDasharray="4 3"
                      />
                    </svg>
                    Converted
                  </span>
                </div>
              </div>
              {byPeriod.length > 0 ? (
                <PipelineTrendChart data={trendData} />
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  No daily breakdown available for the selected range.
                </p>
              )}
            </div>

            <div className={cn(GLASS, 'p-6 animate-slide-up')}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                    Conversion Rate Trend
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Daily conversion percentage
                  </p>
                </div>
                <span className="font-mono text-xs font-semibold tabular-nums text-violet-600 dark:text-violet-400">
                  {conversionRate.toFixed(1)}% avg
                </span>
              </div>
              {byPeriod.length > 0 ? (
                <BarChart data={rateBarData} yAxisLabel="%" />
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  No daily breakdown available for the selected range.
                </p>
              )}
            </div>
          </div>

          {/* Status breakdown (pie + table) */}
          <div className="mb-6">
            <div className={cn(GLASS, 'p-6 animate-slide-up')}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                    Status Breakdown
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Distribution and value by quotation state
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                <div className="lg:col-span-2">
                  {pieData.length > 0 ? (
                    <PieChart data={pieData} donut />
                  ) : (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      No status distribution data.
                    </p>
                  )}
                </div>
                <div className="lg:col-span-3">
                  <StatusBreakdownTable rows={statusRows} totalCount={totalQuotes} />
                  {(approved > 0 || cancelled > 0) && (
                    <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-2 italic">
                      Value for APPROVED &amp; CANCELLED is estimated (avg quote × count);
                      ACTIVE / EXPIRED / CONVERTED are exact sums.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Watchlist row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className={cn(GLASS, 'p-6 animate-slide-up')}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-warning-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-warning-500" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                      Expiring Soon
                    </h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {expiringSoon.length > 0
                        ? `${expiringSoon.length} ${expiringSoon.length === 1 ? 'quote' : 'quotes'} need attention this week`
                        : 'Active quotes within the next 7 days'}
                    </p>
                  </div>
                </div>
                <Link
                  to="/quotations?status=ACTIVE"
                  className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
                >
                  All active →
                </Link>
              </div>
              <ExpiringSoonTable data={expiringSoon} loading={expiringQuery.isLoading} />
            </div>

            <div className={cn(GLASS, 'p-6 animate-slide-up')}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-success-500" />
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                      Recent Conversions
                    </h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Latest quotes converted into bills
                    </p>
                  </div>
                </div>
                <Link
                  to="/quotations?status=CONVERTED"
                  className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
                >
                  All converted →
                </Link>
              </div>
              <RecentConversionsTable data={recentConverted} loading={recentConvertedQuery.isLoading} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function QuotationDashboardPage() {
  return (
    <PageContainer
      title="Quote Dashboard"
      breadcrumbs={[
        { label: 'Quotations', href: '/quotations' },
        { label: 'Dashboard' },
      ]}
    >
      <ErrorBoundary>
        <QuotationDashboardContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
